import { useEffect, useState } from "react";
import { CaretLeft, CaretRight, DownloadSimple, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const IMG_RE = /\.(jpe?g|png|gif|webp)$/i;
export const isImageFile = (f) =>
  (f.content_type || "").startsWith("image/") || IMG_RE.test(f.original_filename || "");

const blobCache = new Map();
async function loadBlobUrl(fileId) {
  if (!blobCache.has(fileId)) {
    blobCache.set(fileId, api.get(`/files/${fileId}/download`, { responseType: "blob" })
      .then(r => URL.createObjectURL(r.data))
      .catch((e) => { blobCache.delete(fileId); throw e; }));
  }
  return blobCache.get(fileId);
}

function Lightbox({ images, urls, index, setIndex, onClose, onDownload }) {
  const f = images[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((index + 1) % images.length);
      if (e.key === "ArrowLeft") setIndex((index - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images.length, onClose, setIndex]);
  return (
    <div className="fixed inset-0 z-[100] flex flex-col" style={{ background: "rgba(0,0,0,.92)" }}
      onClick={onClose} data-testid="sample-lightbox">
      <div className="flex items-center justify-between gap-3 p-4 text-white" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm truncate">{f.original_filename} <span className="opacity-60">· {index + 1}/{images.length}</span></p>
        <div className="flex gap-2">
          <button onClick={() => onDownload(f)} data-testid="lightbox-download"
            className="px-3 py-2 text-xs border border-white/40 flex items-center gap-1 hover:bg-white/10">
            <DownloadSimple size={12} weight="bold" /> Download
          </button>
          <button onClick={onClose} data-testid="lightbox-close" className="px-3 py-2 border border-white/40 hover:bg-white/10">
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center gap-4 px-4 pb-6 min-h-0">
        {images.length > 1 && (
          <button onClick={(e) => { e.stopPropagation(); setIndex((index - 1 + images.length) % images.length); }}
            data-testid="lightbox-prev" className="p-3 text-white border border-white/30 hover:bg-white/10">
            <CaretLeft size={18} weight="bold" />
          </button>
        )}
        {urls[f.file_id]
          ? <img src={urls[f.file_id]} alt={f.original_filename} onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-full object-contain" data-testid="lightbox-image" />
          : <p className="text-white/60 text-sm">Loading…</p>}
        {images.length > 1 && (
          <button onClick={(e) => { e.stopPropagation(); setIndex((index + 1) % images.length); }}
            data-testid="lightbox-next" className="p-3 text-white border border-white/30 hover:bg-white/10">
            <CaretRight size={18} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function SampleGallery({ files, onDownload, testid = "sample-gallery" }) {
  const images = files.filter(isImageFile);
  const [urls, setUrls] = useState({});
  const [open, setOpen] = useState(null);
  const key = images.map(f => f.file_id).join(",");
  useEffect(() => {
    let alive = true;
    images.forEach(f => loadBlobUrl(f.file_id)
      .then(u => alive && setUrls(prev => ({ ...prev, [f.file_id]: u })))
      .catch(() => {}));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  if (!images.length) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2 mt-3" data-testid={testid}>
        {images.map((f, i) => (
          <button key={f.file_id} type="button" onClick={() => setOpen(i)} title={f.original_filename}
            data-testid={`sample-thumb-${f.file_id}`}
            className="w-28 h-28 hair overflow-hidden flex items-center justify-center transition-transform duration-200 hover:scale-[1.03]"
            style={{ border: "1px solid var(--hair)", background: "var(--panel)" }}>
            {urls[f.file_id]
              ? <img src={urls[f.file_id]} alt={f.original_filename} className="w-full h-full object-cover" />
              : <span className="label">Loading…</span>}
          </button>
        ))}
      </div>
      {open !== null && (
        <Lightbox images={images} urls={urls} index={open} setIndex={setOpen}
          onClose={() => setOpen(null)} onDownload={onDownload} />
      )}
    </>
  );
}
